import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';
import {
  AdjustmentContext,
  AdjustmentStatesMap,
  AiSearchAdjustmentDto,
  AiSearchAdjustmentType,
  AiSearchData,
  AiSearchDto,
  AiSearchState,
  SearchState,
  SuggestedFiltersState,
} from '../../../../shared/models';
import { AiSearchService } from '../../../../shared/services';
import { buildAiSearchData, buildAiSearchDto } from '../../shared/utils';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AiSearchHandlerService } from './ai-search-handler.service';

const INITIAL_STATE: AiSearchState = {
  searchId: null,
  data: null,
  savedDto: null,
  isPendingSubmission: false,
  isSearchModified: false,
  isEnabled: true,
  isAdjustmentInProgress: false,
  adjustmentInProgressDocId: null,
  isGlobalSearchSubmitted: false,
  suggestedFilters: null,
  activeSimilarDocIds: new Set(),
  isPartialAdjustmentPatch: false,
};

@Injectable()
export class AiSearchStateService {
  private stateSrc$ = new BehaviorSubject<AiSearchState>({ ...INITIAL_STATE });
  private previousSearchId: string | null = null;
  private submittedEnabled: boolean | null = null;

  readonly state$ = this.stateSrc$.asObservable();

  // Derived selectors (external):
  readonly data$ = this.state$.pipe(
    map((s) => s.data),
    distinctUntilChanged(),
  );
  readonly isPendingSubmission$ = this.state$.pipe(
    map((s) => s.isPendingSubmission),
    distinctUntilChanged(),
  );
  readonly isEnabled$ = this.state$.pipe(
    map((s) => s.isEnabled),
    distinctUntilChanged(),
  );

  readonly isPartialAdjustmentPatch$ = this.state$.pipe(
    map((s) => s.isPartialAdjustmentPatch),
    distinctUntilChanged(),
  );

  readonly isOwner$ = this.state$.pipe(
    map((s) => s.savedDto?.isOwner ?? true),
    distinctUntilChanged(),
  );

  readonly adjustmentContext$: Observable<AdjustmentContext> = this.state$.pipe(
    distinctUntilChanged(
      (prev, curr) =>
        prev.data === curr.data &&
        prev.searchId === curr.searchId &&
        prev.isEnabled === curr.isEnabled &&
        prev.isAdjustmentInProgress === curr.isAdjustmentInProgress &&
        prev.adjustmentInProgressDocId === curr.adjustmentInProgressDocId &&
        prev.activeSimilarDocIds === curr.activeSimilarDocIds,
    ),
    map((s) => ({
      statesMap: this.buildAdjustmentStatesMap(s.data?.adjustments),
      canAdd: !!s.searchId && s.isEnabled,
      isInProgress: s.isAdjustmentInProgress,
      inProgressDocId: s.adjustmentInProgressDocId,
      forbiddenDocIds: s.activeSimilarDocIds,
    })),
  );

  readonly suggestedFilters$ = this.state$.pipe(
    map((s) => s.suggestedFilters),
    distinctUntilChanged(),
  );

  readonly searchStateUpdate$: Observable<SearchState | null> = this.state$.pipe(
    map((s): SearchState | null => {
      const { searchId, isSearchModified, isGlobalSearchSubmitted, isEnabled } = s;

      if (!searchId) {
        return null;
      }

      // Modified AI search or unprocessed adjustments → only blocks when AI is ON
      if (isEnabled && (isSearchModified || this.hasUnprocessedAdjustments(s.data))) {
        return SearchState.NEW_DISABLED;
      }

      // Already submitted AND isEnabled matches submitted value → no config change → SENT
      if (isGlobalSearchSubmitted && isEnabled === this.submittedEnabled) {
        return SearchState.SENT;
      }

      // AI is OFF and not matching submitted → delegate to filters via SearchService
      if (!isEnabled) {
        return null;
      }

      // AI is ON, either not yet submitted or isEnabled changed from submitted
      if (searchId !== this.previousSearchId) {
        return SearchState.NEW;
      }

      if (!isSearchModified) {
        return SearchState.NEW;
      }

      return null;
    }),
    distinctUntilChanged(),
  );

  constructor(
    private aiSearchService: AiSearchService,
    private handlerService: AiSearchHandlerService,
  ) {}

  // ****************** State helpers ******************

  private patchState(partial: Partial<AiSearchState>): void {
    this.stateSrc$.next({ ...this.stateSrc$.getValue(), ...partial });
  }

  private getState(): AiSearchState {
    return this.stateSrc$.getValue();
  }

  // ****************** Setters ******************

  setSavedAiSearch(dto: AiSearchDto): void {
    this.patchState({ savedDto: { ...dto } });
  }

  onAccuracyLevelChanged(dto: AiSearchDto): void {
    const patch: Partial<AiSearchState> = {};

    if (this.getState().isEnabled) {
      patch.isGlobalSearchSubmitted = false;
    }

    if (dto?.accuracyLevel != null) {
      this.patchAccuracyLevel(dto.accuracyLevel, patch);
    } else {
      this.patchState(patch);
    }
  }

  setAiSearchId(id: string | null): void {
    this.previousSearchId = this.getAiSearchId();
    this.patchState({ searchId: id });
  }

  setSearchModified(isModified: boolean): void {
    if (!isModified) {
      this.patchState({ isSearchModified: false });
    } else if (this.getAiSearchId()) {
      const patch: Partial<AiSearchState> = { isSearchModified: true };

      if (this.getState().isEnabled) {
        patch.isGlobalSearchSubmitted = false;
      }

      this.patchState(patch);
    }
  }

  setGlobalSearchSubmitted(isSubmitted: boolean): void {
    if (isSubmitted) {
      this.submittedEnabled = this.getState().isEnabled;
    }
    this.patchState({ isGlobalSearchSubmitted: isSubmitted });
  }

  setData(data: AiSearchData | null): void {
    const id = this.getAiSearchId();
    const resultData: AiSearchData | null = data ? { ...data, id: id ?? undefined } : null;

    this.patchState({
      data: resultData,
      activeSimilarDocIds: this.buildSimilarDocIdsFromAdjustments(resultData?.adjustments),
      isPartialAdjustmentPatch: false,
    });
  }

  updateDataByDto(dto: AiSearchDto): void {
    const data: AiSearchData = buildAiSearchData(dto);

    this.setData(data);
  }

  toggleAiSearch(): void {
    this.patchState({ isEnabled: !this.getState().isEnabled });
  }

  setEnabled(value: boolean): void {
    this.patchState({ isEnabled: value });
  }

  getIsEnabled(): boolean {
    return this.getState().isEnabled;
  }

  startPendingSubmission(): void {
    this.patchState({ isPendingSubmission: true });
  }

  resetPendingSubmission(): void {
    this.patchState({ isPendingSubmission: false });
  }

  clear(): void {
    if (!this.isPendingSubmission()) {
      this.previousSearchId = null;
      this.submittedEnabled = null;
      this.patchState({ ...INITIAL_STATE });
    }
  }

  // ****************** Getters ******************

  getSavedAiSearch(): AiSearchDto | null {
    return this.getState().savedDto;
  }

  getAiSearchId(): string | null {
    return this.getState().searchId;
  }

  getData(): AiSearchData | null {
    return this.getState().data;
  }

  isPendingSubmission(): boolean {
    return this.getState().isPendingSubmission;
  }

  getIsSearchModified(): boolean {
    const state = this.getState();
    return !!state.searchId && state.isSearchModified && state.isEnabled;
  }

  hasAiSearch(): boolean {
    return !!this.getState().searchId;
  }

  getIsOwner(): boolean {
    return this.getState().savedDto?.isOwner ?? true;
  }

  isAdjustmentAddingInProgress(): boolean {
    return this.getState().isAdjustmentInProgress;
  }

  setActiveSimilarDocIds(docIds: Set<string>): void {
    this.patchState({ activeSimilarDocIds: docIds });
  }

  setSuggestedFilters(state: SuggestedFiltersState | null): void {
    this.patchState({ suggestedFilters: state });
  }

  markChipApplied(chipId: string): void {
    const current = this.getState().suggestedFilters;

    if (!current) {
      return;
    }

    const groups = current.groups.map((group) => ({
      ...group,
      mainChips: group.mainChips.map((chip) =>
        chip.id === chipId ? { ...chip, applied: true } : chip,
      ),
      additionalChips: group.additionalChips?.map((chip) =>
        chip.id === chipId ? { ...chip, applied: true } : chip,
      ),
    }));

    this.patchState({ suggestedFilters: { ...current, groups } });
  }

  hideSuggestedFilters(): void {
    const current = this.getState().suggestedFilters;

    if (current) {
      this.patchState({ suggestedFilters: { ...current, visible: false } });
    }
  }

  applyAiSearchResponse(dto: AiSearchDto): void {
    if (dto?.id) {
      this.setSavedAiSearch(dto);
      this.setAiSearchId(dto.id);
      this.updateDataByDto(dto);
      this.setSearchModified(false);

      const previousSuggestedFilters = this.getState().suggestedFilters;
      const filters = this.handlerService.buildSuggestedFilters(dto, previousSuggestedFilters);

      this.setSuggestedFilters(filters);
    }
  }

  /**
   * Apply an edit-endpoint response. Returns the new id when the search was
   * forked (backend created a new search because the current user is not the
   * owner) so the caller can navigate to it; returns null otherwise.
   */
  applyEditResponse(dto: AiSearchDto): string | null {
    const currentId = this.getAiSearchId();
    const forkedId = dto?.id && dto.id !== currentId ? dto.id : null;

    this.applyAiSearchResponse(dto);

    if (forkedId) {
      // Fork produced a brand-new search id that has never been submitted to
      // the global /profile/search endpoint — clear the submitted flag so the
      // sidebar footer falls out of SENT and shows the SEARCH button.
      this.patchState({ isGlobalSearchSubmitted: false });
    }

    return forkedId;
  }

  patchAdjustmentsFromDto(dto: AiSearchDto): void {
    if (dto?.adjustments) {
      this.patchDataAdjustments(dto.adjustments);
    }
  }

  // ****************** Actions ******************

  processPendingSubmission(): Observable<string | null> {
    if (!this.isPendingSubmission()) {
      return of(null);
    }

    const formData = this.getData();

    if (!formData) {
      this.clear();
      return of(null);
    }

    const dto = buildAiSearchDto(formData);

    return this.aiSearchService.createAiSearch(dto).pipe(
      tap({
        next: (response: AiSearchDto) => {
          this.applyAiSearchResponse(response);
          this.resetPendingSubmission();
        },
        error: () => {
          this.resetPendingSubmission();
        },
      }),
      map((response: AiSearchDto) => response?.id || null),
      catchError(() => of(null)),
    );
  }

  /**
   * Adds, toggles, or deletes a like/dislike/similar adjustment.
   *
   * Returns the raw {@link AiSearchDto} from the server so the caller can detect
   * a fork (`dto.id !== currentSearchId`) when the user is not the owner. For
   * the owner path, this method already applied a partial adjustments-only
   * patch — the caller should NOT trigger a full state replace in that case
   * (it would reset form fields the partial patch is designed to preserve).
   */
  updateAdjustment(
    type: AiSearchAdjustmentType | null,
    docId: string,
  ): Observable<AiSearchDto | null> {
    if (this.isAdjustmentAddingInProgress()) {
      return of(null);
    }

    const aiSearchId = this.getAiSearchId();
    const isDeleteAction: boolean = type === null;

    if (!aiSearchId || !docId) {
      return of(null);
    }

    this.patchState({ isAdjustmentInProgress: true, adjustmentInProgressDocId: docId });

    const existingType = this.getExistingAdjustmentType(docId);
    const request$: Observable<AiSearchDto | null> = isDeleteAction
      ? this.aiSearchService.deleteAdjustment(aiSearchId, existingType, docId)
      : this.aiSearchService.addAdjustment(aiSearchId, type, docId);

    return request$.pipe(
      tap((dto: AiSearchDto | null) => {
        const idChanged = !!dto?.id && dto.id !== aiSearchId;

        if (dto?.adjustments && !idChanged) {
          this.patchDataAdjustments(dto.adjustments);
        }

        this.patchState({ isAdjustmentInProgress: false, adjustmentInProgressDocId: null });
      }),
      catchError(() => {
        this.patchState({ isAdjustmentInProgress: false, adjustmentInProgressDocId: null });
        return of(null);
      }),
    );
  }

  // ****************** Private ******************

  private patchAccuracyLevel(accuracyLevel: number, basePatch: Partial<AiSearchState> = {}): void {
    const savedDto = this.getSavedAiSearch();
    const patch: Partial<AiSearchState> = { ...basePatch };

    if (savedDto) {
      patch.savedDto = { ...savedDto, accuracyLevel };
    }

    this.patchState(patch);
  }

  private patchDataAdjustments(adjustments: AiSearchAdjustmentDto[]): void {
    const currentData = this.getData();
    const savedDto = this.getSavedAiSearch();
    const patch: Partial<AiSearchState> = {
      activeSimilarDocIds: this.buildSimilarDocIdsFromAdjustments(adjustments),
      isPartialAdjustmentPatch: true,
    };

    if (currentData) {
      patch.data = { ...currentData, adjustments: [...adjustments] };
    }

    if (savedDto) {
      patch.savedDto = { ...savedDto, adjustments: [...adjustments] };
    }

    this.patchState(patch);
  }

  private hasUnprocessedAdjustments(data: AiSearchData | null): boolean {
    return !!data?.adjustments?.some(
      (a: AiSearchAdjustmentDto) => a.status === 'ACTIVE' && !a.processed,
    );
  }

  private getExistingAdjustmentType(docId: string): AiSearchAdjustmentType | null {
    const adjustments = this.getState().data?.adjustments;

    if (!adjustments?.length) {
      return null;
    }

    const existing = adjustments.find(
      (a: AiSearchAdjustmentDto) =>
        a.docId === docId && a.status === 'ACTIVE' && (a.type === 'like' || a.type === 'dislike'),
    );

    return existing?.type || null;
  }

  private buildSimilarDocIdsFromAdjustments(
    adjustments: AiSearchAdjustmentDto[] | undefined,
  ): Set<string> {
    if (!adjustments?.length) {
      return new Set();
    }

    return adjustments.reduce<Set<string>>((acc, a) => {
      if (a.docId && a.status === 'ACTIVE' && a.type === 'similar') {
        acc.add(a.docId);
      }
      return acc;
    }, new Set());
  }

  private buildAdjustmentStatesMap(
    adjustments: AiSearchAdjustmentDto[] | undefined,
  ): AdjustmentStatesMap {
    if (!adjustments?.length) {
      return {};
    }

    return adjustments.reduce((acc: AdjustmentStatesMap, adjustment: AiSearchAdjustmentDto) => {
      const { type, docId, status } = adjustment;

      if (docId && status === 'ACTIVE') {
        return { ...acc, [docId]: type };
      }
      return acc;
    }, {});
  }
}
